import { SWRFetcher } from '@commenda/utils';
import { FORM_BUILDER_TEMPLATE_KEYS_ON_SELECT_DEFAULT_PAGE_SIZE } from '@commenda/utils/form-builder';
import { useCallback, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { BASE_URL } from './helpers';
import {
  FormTemplateKeysListResponse,
  GetFormTemplateKeysQueryDto,
  IFormTemplateKey,
} from './types';

type InfiniteOptions = Omit<GetFormTemplateKeysQueryDto, 'offset'> & {
  pageSize?: number;
  enabled?: boolean;
};

function buildQueryString(
  params: GetFormTemplateKeysQueryDto & { pageSize?: number }
): string {
  const query = new URLSearchParams();

  if (params.liveFormId) {
    query.append('liveFormId', params.liveFormId);
  }

  if (params.hasLiveForm !== undefined) {
    query.append('hasLiveForm', String(params.hasLiveForm));
  }

  if (params.key && params.key.length > 0) {
    params.key.forEach((k) => query.append('key', k));
  }

  if (params.searchTerm) {
    query.append('searchTerm', params.searchTerm);
  }

  if (params.offset !== undefined) {
    query.append('offset', params.offset.toString());
  }

  if (params.limit !== undefined) {
    query.append('limit', params.limit.toString());
  }

  const queryString = query.toString();
  return queryString ? `${BASE_URL}?${queryString}` : BASE_URL;
}

/**
 * Hook to fetch form template keys with infinite scroll support using regular SWR
 */
export const useFormTemplateKeysInfinite = (options?: InfiniteOptions) => {
  const {
    pageSize = FORM_BUILDER_TEMPLATE_KEYS_ON_SELECT_DEFAULT_PAGE_SIZE,
    enabled = true,
    ...filters
  } = options ?? {};

  // Track accumulated items and current offset
  const [accumulatedKeys, setAccumulatedKeys] = useState<IFormTemplateKey[]>(
    []
  );
  const [offset, setOffset] = useState(0);
  const prevFiltersRef = useRef<string>('');

  // Create a stable filter key to detect filter changes
  const filterKey = JSON.stringify(filters);

  // Reset accumulated data when filters change
  useEffect(() => {
    if (prevFiltersRef.current !== filterKey) {
      setAccumulatedKeys([]);
      setOffset(0);
      prevFiltersRef.current = filterKey;
    }
  }, [filterKey]);

  // Build the SWR key
  const swrKey = enabled
    ? buildQueryString({
        ...filters,
        limit: pageSize,
        offset,
      })
    : null;

  const { data, error, mutate, isLoading, isValidating } =
    useSWR<FormTemplateKeysListResponse>(
      swrKey,
      SWRFetcher({ method: 'GET' }),
      {
        revalidateOnFocus: false,
      }
    );

  // Accumulate new keys when data changes
  useEffect(() => {
    if (data?.formTemplateKeys) {
      setAccumulatedKeys((prev) => {
        // If offset is 0, replace all data (fresh fetch or filter change)
        if (offset === 0) {
          return data.formTemplateKeys;
        }
        // Otherwise append new keys, avoiding duplicates by key
        const existingKeys = new Set(prev.map((k) => k.key));
        const newKeys = data.formTemplateKeys.filter(
          (k) => !existingKeys.has(k.key)
        );
        return [...prev, ...newKeys];
      });
    }
  }, [data, offset]);

  const totalCount = data?.count ?? 0;
  const hasNextPage = accumulatedKeys.length < totalCount;

  const loadMore = useCallback(() => {
    if (hasNextPage && !isLoading && !isValidating) {
      setOffset(accumulatedKeys.length);
    }
  }, [hasNextPage, isLoading, isValidating, accumulatedKeys.length]);

  const reset = useCallback(() => {
    setAccumulatedKeys([]);
    setOffset(0);
    mutate();
  }, [mutate]);

  return {
    keys: accumulatedKeys,
    totalCount,
    hasNextPage,
    loadMore,
    reset,
    error,
    mutate,
    isLoading: isLoading && accumulatedKeys.length === 0,
    isLoadingMore: isLoading && accumulatedKeys.length > 0,
    isValidating,
  };
};
